import type { ConversationCreateMessageDatagramBuf } from "~/classes/generated/message"
import type { ConversationCreateResponseDatagramBuf } from "~/classes/generated/response"

import { StaticConversation } from "~/classes/conversation/StaticConversation"
import { DatagramBuf } from "~/classes/generated/datagram"
import { ConversationCreateRequestDatagramBuf, RequestDatagramBuf } from "~/classes/generated/request"
import { DatagramBufferBuilder } from "~/utils/flatbuffers/builder"
import { addConversationToBuf, parseConversation } from "~/utils/flatbuffers/conversation"
import { addRequestIdToBuf, parseDatagramError, parseMessageId, parseRequestId, parseSenderId } from "~/utils/flatbuffers/datagram"
import { addConversationIdToBuf, parseConversationId } from "~/utils/flatbuffers/id"
import { maybe } from "~/utils/helpers"

const console = useLogger("conversation/create")

export function sendConversationCreateRequests(requests: ConversationCreateRequestDatagram[]) {
  const { sendWorkerDatagramBuf } = useCache()

  const builder = new DatagramBufferBuilder(64)
  const createRequests = []
  for (const request of requests) {
    const requestId = addRequestIdToBuf(builder, request.requestId)
    const preHash = addConversationIdToBuf(builder, request.preHash)
    const conversation = addConversationToBuf(builder, request.conversation)

    ConversationCreateRequestDatagramBuf.startConversationCreateRequestDatagramBuf(builder)
    ConversationCreateRequestDatagramBuf.addRequestId(builder, requestId)
    ConversationCreateRequestDatagramBuf.addOffline(builder, request.offline)
    ConversationCreateRequestDatagramBuf.addPreHash(builder, preHash)
    ConversationCreateRequestDatagramBuf.addConversation(builder, conversation)
    const createRequest = ConversationCreateRequestDatagramBuf.endConversationCreateRequestDatagramBuf(builder)

    RequestDatagramBuf.startRequestDatagramBuf(builder)
    RequestDatagramBuf.addConversationCreateRequest(builder, createRequest)
    createRequests.push(RequestDatagramBuf.endRequestDatagramBuf(builder))
  }

  const create = DatagramBuf.createRequestVector(builder, createRequests)
  DatagramBuf.startDatagramBuf(builder)
  DatagramBuf.addRequest(builder, create)
  builder.finish(DatagramBuf.endDatagramBuf(builder))
  sendWorkerDatagramBuf(builder.asUint8Array())
}

/** Parse a Flatbuffers representation of a response */
export function handleConversationCreateResponseDatagramBuf(datagram: ConversationCreateResponseDatagramBuf) {
  onConversationCreateResponse({
    requestId: parseRequestId(datagram.requestId()!),
    error: maybe(parseDatagramError)(datagram.error()),
    conversation: maybe(parseConversation)(datagram.conversation()),
    offline: datagram.offline(),
    conversationId: parseConversationId(datagram.conversationId()!),
  })
}

export function onConversationCreateResponse(response: ConversationCreateResponseDatagram) {
  const { emitEntityEvent, emitInterfaceEvent } = useEvents()

  console.debug("Received conversation create", response)

  emitInterfaceEvent("conversationCreateResponse", response, response.requestId)

  if (response.error)
    return onConversationCreateErrored(response.error)
  if (!response.conversation)
    throw new Error("No conversation in create response")

  const iConversation = new StaticConversation(response.conversation)
  emitEntityEvent("conversationCreated", iConversation.id, iConversation.inner(), response.offline)
  emitEntityEvent("conversationProperties", iConversation.id, iConversation.inner(), response.offline)
}

function onConversationCreateErrored(error: DatagramError) {
  const { notifyDatagramError } = useAlerts()

  return notifyDatagramError(error, "Conversation create request was unsuccessful")
}

export function handleConversationCreateMessageDatagramBuf(datagram: ConversationCreateMessageDatagramBuf) {
  onConversationCreateMessage({
    messageId: parseMessageId(datagram.messageId()!),
    senderId: parseSenderId(datagram.senderId()!),
    conversation: parseConversation(datagram.conversation()!),
  })
}

export function onConversationCreateMessage(message: ConversationCreateMessageDatagram) {
  const { emitEntityEvent } = useEvents()

  console.debug("Received conversation create message", clone(message))

  const iConversation = new StaticConversation(message.conversation)
  emitEntityEvent("conversationCreated", iConversation.id, iConversation.inner(), false)
  emitEntityEvent("conversationProperties", iConversation.id, iConversation.inner(), false)
}
